import { Link as RouterLink } from 'react-router-dom';

import { Box, Link, Table, TableBody, TableCell, TableHead, TablePagination, TableRow } from '@mui/material';

import { Organization, OrgSortColumn, SortOrder } from '../types';
import { SortableTableHeader } from './SortableTableHeader';
import { SearchAndPagination } from './SearchAndPagination';

// Fonction utilitaire pour vérifier si le code postal est manquant
const isPostCodeMissing = (postCode: string | null | undefined): boolean => {
  return !postCode || postCode.trim() === '' || postCode === '0' || postCode === '00000';
};

// Fonction pour vérifier si une organisation a des données manquantes critiques
const hasMissingCriticalData = (org: Organization): boolean => {
  // Vérifier si l'email est manquant
  const isEmailMissing = !org.email || org.email.trim() === '';

  // Vérifier si le SIRET est manquant
  const isSiretMissing = !org.siret || org.siret.trim() === '';

  // Vérifier si le code postal est manquant
  const isPostCodeMissingValue = isPostCodeMissing(org.post_code);

  // Vérifier si la ville est manquante
  const isTownMissing = !org.town || org.town.trim() === '';

  // Retourner true si au moins une des données critiques est manquante
  return isEmailMissing || isSiretMissing || isPostCodeMissingValue || isTownMissing;
};

const stickyCellStyle: React.CSSProperties = {
  background: '#262626',
  fontWeight: 'bold',
  minWidth: 50,
  color: 'white',
  position: 'sticky',
  top: 0,
  zIndex: 100,
};

interface OrganizationsTableProps {
  organizations: Organization[];
  totalCount: number;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  sortColumn: OrgSortColumn;
  sortOrder: SortOrder;
  onSort: (column: OrgSortColumn) => void;
  page: number;
  rowsPerPage: number;
  onPageChange: (event: unknown, newPage: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const OrganizationsTable = ({
  organizations,
  totalCount,
  searchQuery,
  onSearchChange,
  sortColumn,
  sortOrder,
  onSort,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
}: OrganizationsTableProps) => {
  return (
    <Box>
      <SearchAndPagination
        searchQuery={searchQuery}
        onSearchChange={onSearchChange}
        totalCount={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
      />
      <Table stickyHeader size="small">
        <TableHead className="stickyTableHead">
          <TableRow sx={{ background: '#262626' }}>
              <SortableTableHeader
                label="ID"
                sortColumn="id"
                currentSortColumn={sortColumn}
                sortOrder={sortOrder}
                onSort={() => onSort('id')}
                stickyCellStyle={stickyCellStyle}
              />
              <SortableTableHeader
                label="Nom"
                sortColumn="name"
                currentSortColumn={sortColumn}
                sortOrder={sortOrder}
                onSort={() => onSort('name')}
                stickyCellStyle={stickyCellStyle}
              />
              <SortableTableHeader
                label="Entité légale"
                sortColumn="legal_entity"
                currentSortColumn={sortColumn}
                sortOrder={sortOrder}
                onSort={() => onSort('legal_entity')}
                stickyCellStyle={stickyCellStyle}
              />
              <TableCell sx={stickyCellStyle}>Adresse</TableCell>
              <SortableTableHeader
                label="Code postal"
                sortColumn="post_code"
                currentSortColumn={sortColumn}
                sortOrder={sortOrder}
                onSort={() => onSort('post_code')}
                stickyCellStyle={stickyCellStyle}
              />
              <SortableTableHeader
                label="Ville"
                sortColumn="town"
                currentSortColumn={sortColumn}
                sortOrder={sortOrder}
                onSort={() => onSort('town')}
                stickyCellStyle={stickyCellStyle}
              />
              <TableCell sx={stickyCellStyle}>Pays</TableCell>
              <TableCell sx={stickyCellStyle}>Téléphone</TableCell>
              <SortableTableHeader
                label="Email"
                sortColumn="email"
                currentSortColumn={sortColumn}
                sortOrder={sortOrder}
                onSort={() => onSort('email')}
                stickyCellStyle={stickyCellStyle}
              />
              <TableCell sx={stickyCellStyle}>SIRET</TableCell>
              <TableCell sx={stickyCellStyle}>Franchise ID</TableCell>
              <TableCell sx={stickyCellStyle}>Contact</TableCell>
              <TableCell sx={stickyCellStyle}>Person ID</TableCell>
              <TableCell sx={stickyCellStyle}>Person Name</TableCell>
              <TableCell sx={stickyCellStyle}>Person Email</TableCell>
              <TableCell sx={stickyCellStyle}>Supprimé</TableCell>
              <SortableTableHeader
                label="Mis à jour"
                sortColumn="updated_at"
                currentSortColumn={sortColumn}
                sortOrder={sortOrder}
                onSort={() => onSort('updated_at')}
                stickyCellStyle={stickyCellStyle}
              />
            </TableRow>
          </TableHead>
          <TableBody>
            {organizations.map((org) => {
              const isPostCodeMissingValue = isPostCodeMissing(org.post_code);
              const hasMissingData = hasMissingCriticalData(org);
              return (
                <TableRow
                  key={org.id}
                  sx={{
                    backgroundColor: hasMissingData ? 'rgba(255, 0, 0, 0.15)' : 'inherit',
                    '&:hover': {
                      backgroundColor: hasMissingData ? 'rgba(255, 0, 0, 0.25)' : 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  <TableCell>
                <Link component={RouterLink} to={`/admin/agency/${org.id}`}>
                  {org.id}
                </Link>
              </TableCell>
                  <TableCell>{org.name}</TableCell>
                  <TableCell>{org.legal_entity}</TableCell>
                  <TableCell>{org.address}</TableCell>
                  <TableCell
                    sx={{
                      color: isPostCodeMissingValue ? 'red' : 'inherit',
                      fontWeight: isPostCodeMissingValue ? 'bold' : 'normal',
                    }}
                  >
                    {org.post_code || '-'}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: !org.town || org.town.trim() === '' ? 'red' : 'inherit',
                      fontWeight: !org.town || org.town.trim() === '' ? 'bold' : 'normal',
                    }}
                  >
                    {org.town || '-'}
                  </TableCell>
                  <TableCell>{org.country}</TableCell>
                  <TableCell>{org.tel}</TableCell>
                  <TableCell
                    sx={{
                      color: !org.email || org.email.trim() === '' ? 'red' : 'inherit',
                      fontWeight: !org.email || org.email.trim() === '' ? 'bold' : 'normal',
                    }}
                  >
                    {org.email || '-'}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: !org.siret || org.siret.trim() === '' ? 'red' : 'inherit',
                      fontWeight: !org.siret || org.siret.trim() === '' ? 'bold' : 'normal',
                    }}
                  >
                    {org.siret || '-'}
                  </TableCell>
                  <TableCell>{org.franchise_id}</TableCell>
                  <TableCell>{org.last_contact_name}</TableCell>
                  <TableCell>{org.person_id}</TableCell>
                  <TableCell>{org.person_name}</TableCell>
                  <TableCell>{org.person_email}</TableCell>
                  <TableCell>{org.deleted_at ? 'Oui' : 'Non'}</TableCell>
                  <TableCell>{org.updated_at ? new Date(org.updated_at).toLocaleString('fr-FR') : '-'}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          padding: 2,
        }}
      >
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          labelRowsPerPage="Lignes par page"
          labelDisplayedRows={({ from, to }) => `de ${from} à ${to}`}
        />
      </Box>
    </Box>
  );
};

